import { useEffect, useState } from "react";

import { useProducts } from "@/hooks/use-products";

/** Shorter text matches half the catalog; the searches ask for more instead. */
export const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 250;

interface ProductSearchOptions {
  /** Off: nothing goes to the server, e.g. while the header's list is closed. */
  enabled?: boolean;
  pageSize: number;
}

/**
 * Search-as-you-type over the catalog: asks the server once per pause in typing, never for
 * text shorter than MIN_SEARCH_LENGTH, and tells whether the results it has answer the text
 * as typed now.
 */
export function useProductSearch(text: string, { enabled = true, pageSize }: ProductSearchOptions) {
  const typed = text.trim();
  // Trimmed, so a trailing space isn't a new search.
  const [debounced, setDebounced] = useState(typed);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(typed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const isTooShort = typed.length < MIN_SEARCH_LENGTH;
  const { data, isFetching, isPlaceholderData } = useProducts(
    { search: debounced, pageSize },
    enabled && debounced.length >= MIN_SEARCH_LENGTH,
  );
  // The results lag behind the typing twice: through the debounce, and while the server
  // answers the new text (useProducts keeps the previous answer meanwhile).
  const answersCurrentText = !isTooShort && typed === debounced && !isPlaceholderData;

  return {
    isTooShort,
    /** The latest results, possibly for older text: see `answersCurrentText`. */
    items: data?.items ?? [],
    answersCurrentText,
    /** No settled answer for the current text yet, so "nothing found" would be premature. */
    isSearching: !isTooShort && (!answersCurrentText || isFetching),
  };
}
